// Workspace state: the open source, what was discovered in it, loaded models and the selection.
import { assignMigration, canonicalJson, discoverMigrationSets, hash53, type DiscoveryResult, type MigrationSetInfo, type SourceFileEntry } from '@modelviewer/core';
import { create } from 'zustand';
import { db, type NoteRecord, type SketchRecord } from '../lib/db';
import type { SourceChange, WorkspaceSource } from '../sources/types';
import type { ModelResult } from '../workers/parse.worker';
import { parser } from '../workers/parseClient';

/** Which model to show: a migration of a context, its current model (the snapshot), or a sketch. */
export interface ModelRef {
  setKey: string;
  migrationId: string | null;
  /** A sketch kept in the browser; setKey is then the context it belongs to ('' if none). */
  sketchId?: string;
}

export const refKey = (ref: ModelRef) => (ref.sketchId ? `sketch:${ref.sketchId}` : `${ref.setKey}|${ref.migrationId ?? 'current'}`);

export const sketchRef = (s: Pick<SketchRecord, 'id' | 'setKey'>): ModelRef => ({ setKey: s.setKey ?? '', migrationId: null, sketchId: s.id });

export interface LoadedModel {
  ref: ModelRef;
  file: string;
  textHash: string;
  result: ModelResult;
}

export type Selection =
  | { kind: 'entity'; key: string }
  | { kind: 'property'; entity: string; property: string }
  | { kind: 'relationship'; index: number };

export interface WorkspaceState {
  source: WorkspaceSource | null;
  files: SourceFileEntry[];
  discovery: DiscoveryResult | null;
  busy: string | null;
  error: string | null;
  models: Record<string, LoadedModel>;
  modelErrors: Record<string, string>;
  selection: { ref: ModelRef; value: Selection } | null;
  lastChange: { at: number; summary: string; paths: string[] } | null;
  /** The model shown in the active diagram tab. */
  activeRef: ModelRef | null;
  /** The timeline step shown in the active tab, when it is a timeline. */
  activeStep: { setKey: string; stepId: string } | null;
  /** A request to center a diagram on an entity (from the explorer or the command palette), to focus on it, or to show what deleting one of its rows does. */
  reveal: { ref: ModelRef; key: string; nonce: number; focus?: boolean; deletion?: boolean } | null;
  /** Sketches of the open source, by id. */
  sketches: Record<string, SketchRecord>;
  /** Notes of the open source, by id. */
  notes: Record<string, NoteRecord>;
  /** The note shown in the active tab, when it is a note. */
  activeNote: string | null;
}

export const useWorkspace = create<WorkspaceState>(() => ({
  source: null,
  files: [],
  discovery: null,
  busy: null,
  error: null,
  models: {},
  modelErrors: {},
  selection: null,
  lastChange: null,
  activeRef: null,
  activeStep: null,
  reveal: null,
  sketches: {},
  notes: {},
  activeNote: null,
}));

let stopWatching: (() => void) | null = null;

/**
 * Reads what agents wrote in `.modelviewer/` (see folderItems.ts). Registered from there, so this
 * module does not depend on it; called before the discovery is published and after each change.
 */
export type FolderSync = (source: WorkspaceSource, files: SourceFileEntry[], discovery: DiscoveryResult, change: SourceChange | null) => Promise<void>;
let folderSync: FolderSync | null = null;

export function registerFolderSync(sync: FolderSync): void {
  folderSync = sync;
}
const inflight = new Map<string, Promise<LoadedModel | null>>();

export function findSet(key: string): MigrationSetInfo | undefined {
  return useWorkspace.getState().discovery?.sets.find((s) => s.key === key);
}

/** The file that holds a model: the snapshot for "current", otherwise the migration's Designer file. */
export function modelFile(ref: ModelRef): string | null {
  const set = findSet(ref.setKey);
  if (!set) return null;
  if (ref.migrationId === null) return set.snapshotPath ?? set.migrations.at(-1)?.designerPath ?? null;
  return set.migrations.find((m) => m.id === ref.migrationId)?.designerPath ?? null;
}

async function discover(source: WorkspaceSource, files: SourceFileEntry[]): Promise<DiscoveryResult> {
  const discovery = discoverMigrationSets(files);
  // Migrations in a folder shared by several snapshots: their [DbContext] attribute decides.
  for (const pending of [...discovery.unassigned]) {
    const file = pending.migration.designerPath ?? pending.migration.mainPath;
    if (!file) continue;
    try {
      const info = await parser().describe(await source.read(file));
      if (info.contextType) assignMigration(discovery, pending.migration.id, info.contextType);
    } catch {
      // Leave it unassigned; it is listed in the status bar.
    }
  }
  return discovery;
}

export async function openSource(source: WorkspaceSource): Promise<void> {
  stopWatching?.();
  stopWatching = null;
  inflight.clear();
  extraFolders = [];
  useWorkspace.setState({
    source,
    files: [],
    discovery: null,
    busy: `Reading ${source.label}…`,
    error: null,
    models: {},
    modelErrors: {},
    selection: null,
    lastChange: null,
    activeRef: null,
    activeStep: null,
    reveal: null,
    sketches: {},
    notes: {},
    activeNote: null,
  });
  try {
    const files = await source.list((count) => useWorkspace.setState({ busy: `Reading ${source.label}… ${count} files` }));
    const discovery = await discover(source, files);
    // Sketches first: restoring the saved tabs (when discovery is set) checks that they exist.
    for (const sketch of await db.sketches.where('sourceId').equals(source.id).toArray()) putSketch(sketch);
    const notes = Object.fromEntries((await db.notes.where('sourceId').equals(source.id).toArray()).map((n) => [n.id, n]));
    useWorkspace.setState({ notes });
    await folderSync?.(source, files, discovery, null);
    useWorkspace.setState({ files, discovery, busy: null });
    if (source.live && source.watch) startWatching(source, discovery);
  } catch (e) {
    useWorkspace.setState({ busy: null, error: `Could not read ${source.label}: ${(e as Error).message}` });
  }
}

export function closeSource(): void {
  stopWatching?.();
  stopWatching = null;
  inflight.clear();
  extraFolders = [];
  useWorkspace.setState({ source: null, files: [], discovery: null, busy: null, error: null, models: {}, modelErrors: {}, selection: null, lastChange: null, activeRef: null, activeStep: null, reveal: null, sketches: {}, notes: {}, activeNote: null });
}

/** More folders to watch while a view needs them (the code map's folders); see setExtraWatchFolders. */
let extraFolders: string[] = [];

/** Watches these folders too (replacing the previous extra folders), so their files reload live. */
export function setExtraWatchFolders(folders: string[]): void {
  const next = [...new Set(folders)].sort();
  if (next.length === extraFolders.length && next.every((f, i) => f === extraFolders[i])) return;
  extraFolders = next;
  const { source, discovery } = useWorkspace.getState();
  if (stopWatching && source?.live && source.watch && discovery) {
    stopWatching();
    startWatching(source, discovery);
  }
}

function watchTargets(discovery: DiscoveryResult) {
  const folders = new Set<string>(['.modelviewer', ...extraFolders]);
  const files = new Set<string>();
  const dirOf = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');
  for (const set of discovery.sets) {
    folders.add(set.folder);
    if (set.snapshotPath) files.add(set.snapshotPath);
    for (const m of set.migrations) {
      if (m.mainPath) folders.add(dirOf(m.mainPath));
      if (m.designerPath) folders.add(dirOf(m.designerPath));
    }
  }
  return { folders: [...folders], files: [...files] };
}

function startWatching(source: WorkspaceSource, discovery: DiscoveryResult): void {
  stopWatching = source.watch!(watchTargets(discovery), (change) => void applyChange(change));
}

async function applyChange(change: SourceChange): Promise<void> {
  const state = useWorkspace.getState();
  const source = state.source;
  if (!source) return;
  const removed = new Set(change.removed);
  const known = new Set(state.files.map((f) => f.path));
  const files = state.files.filter((f) => !removed.has(f.path));
  for (const path of change.added) if (!known.has(path)) files.push({ path });
  files.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  const discovery = await discover(source, files);
  await folderSync?.(source, files, discovery, change);

  // Reload models whose file changed or disappeared.
  const touched = new Set([...change.changed, ...change.removed]);
  const models = { ...useWorkspace.getState().models };
  const modelErrors = { ...useWorkspace.getState().modelErrors };
  for (const [key, loaded] of Object.entries(models)) {
    // Folder sketches were just read again by the folder sync.
    if (touched.has(loaded.file) && !loaded.ref.sketchId) {
      delete models[key];
      delete modelErrors[key];
      inflight.delete(key);
    }
  }
  const parts = [
    change.added.length ? `${change.added.length} added` : '',
    change.changed.length ? `${change.changed.length} changed` : '',
    change.removed.length ? `${change.removed.length} removed` : '',
  ].filter(Boolean);
  useWorkspace.setState({ files, discovery, models, modelErrors, lastChange: { at: Date.now(), summary: parts.join(', '), paths: [...change.added, ...touched] } });

  // New migration folders must be watched too.
  if (change.added.length && source.watch) {
    stopWatching?.();
    startWatching(source, discovery);
  }
}

/** Shows a sketch's current document as a loaded model (sketches are edited in memory, not read from files). */
export function putSketch(sketch: SketchRecord): void {
  const ref = sketchRef(sketch);
  const loaded: LoadedModel = {
    ref,
    file: sketch.origin?.path ?? '',
    textHash: hash53(canonicalJson(sketch.doc)),
    result: { doc: sketch.doc, diagnostics: [], lines: { entities: {}, properties: {} }, efVersion: null, contextType: null },
  };
  useWorkspace.setState((s) => ({ sketches: { ...s.sketches, [sketch.id]: sketch }, models: { ...s.models, [refKey(ref)]: loaded } }));
}

export function dropSketch(id: string): void {
  useWorkspace.setState((s) => {
    const sketches = { ...s.sketches };
    const models = { ...s.models };
    delete sketches[id];
    delete models[`sketch:${id}`];
    const selection = s.selection?.ref.sketchId === id ? null : s.selection;
    return { sketches, models, selection };
  });
}

/** Loads (or returns the cached) model for a reference. */
export function loadModel(ref: ModelRef): Promise<LoadedModel | null> {
  const key = refKey(ref);
  const existing = useWorkspace.getState().models[key];
  if (existing) return Promise.resolve(existing);
  if (ref.sketchId) {
    useWorkspace.setState((s) => ({ modelErrors: { ...s.modelErrors, [key]: 'This sketch no longer exists.' } }));
    return Promise.resolve(null);
  }
  const running = inflight.get(key);
  if (running) return running;
  const task = (async () => {
    const { source } = useWorkspace.getState();
    const set = findSet(ref.setKey);
    const file = modelFile(ref);
    if (!source || !set || !file) {
      const message = !file && ref.migrationId ? 'This migration has no .Designer.cs file, so its model is unknown.' : 'Model not found.';
      useWorkspace.setState((s) => ({ modelErrors: { ...s.modelErrors, [key]: message } }));
      return null;
    }
    try {
      const text = await source.read(file);
      const textHash = hash53(text);
      // Same content already parsed under another reference (for example the latest migration and the snapshot).
      const same = Object.values(useWorkspace.getState().models).find((m) => m.textHash === textHash && m.file === file);
      const result =
        same?.result ??
        (await parser().model(text, {
          title: `${set.contextName} @ ${ref.migrationId ?? 'current'}`,
          context: set.contextName,
          migration: ref.migrationId ?? undefined,
          file,
        }));
      const loaded: LoadedModel = { ref, file, textHash, result };
      useWorkspace.setState((s) => ({ models: { ...s.models, [key]: loaded } }));
      return loaded;
    } catch (e) {
      useWorkspace.setState((s) => ({ modelErrors: { ...s.modelErrors, [key]: (e as Error).message } }));
      return null;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, task);
  return task;
}

export function select(ref: ModelRef, value: Selection | null): void {
  useWorkspace.setState({ selection: value ? { ref, value } : null });
}

export function setActiveRef(ref: ModelRef | null, step: { setKey: string; stepId: string } | null = null): void {
  const current = useWorkspace.getState().activeStep;
  const same = current === step || (current && step && current.setKey === step.setKey && current.stepId === step.stepId);
  useWorkspace.setState(same ? { activeRef: ref, activeNote: null } : { activeRef: ref, activeStep: step, activeNote: null });
}

/**
 * Selects an entity and asks its diagram to bring it into view, to show it in focus (it and what it
 * relates to), or to turn cascade paths on for it: what deleting one of its rows does (`deletion`).
 */
export function revealEntity(ref: ModelRef, key: string, options: { focus?: boolean; deletion?: boolean } = {}): void {
  useWorkspace.setState({ selection: { ref, value: { kind: 'entity', key } }, reveal: { ref, key, nonce: Date.now(), focus: options.focus, deletion: options.deletion } });
}
